import { Calendar } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export interface DayOption {
  date: string;
  label: string;
  disabled: boolean;
  /** Por qué está bloqueado (se muestra en rojo). */
  reason?: string;
  /** Aviso para un día habilitado (p.ej. ruta en un solo turno). */
  hint?: string;
}

interface Props {
  days: DayOption[];
  selected: string[];
  onToggle: (date: string) => void;
  /** En edición: se muestra este único día, fijo, sin checkboxes. */
  editDateLabel?: string;
  /** En edición: aviso de ruta parcial para el día fijo (mismo motivo que en creación). */
  editHint?: string;
}

export const DayPicker = ({ days, selected, onToggle, editDateLabel, editHint }: Props) => (
  <div className="mb-6">
    <Label className="block mb-3 text-lg font-medium">
      {editDateLabel !== undefined
        ? "Día seleccionado"
        : "Seleccione los días de la semana"}
    </Label>
    <div className="grid grid-cols-1 gap-2">
      {editDateLabel !== undefined ? (
        // En modo edición, mostrar solo el día específico
        <div className="border-2 border-primary bg-primary-soft rounded-lg p-3">
          <div className="flex items-center space-x-3">
            <Calendar className="h-4 w-4 text-primary" />
            {/* translate="no": evita que el traductor de Chrome
                reinterprete la fecha (ver formatLongDate) */}
            <span className="font-medium notranslate" translate="no">
              {editDateLabel}
            </span>
          </div>
          {editHint && (
            <div className="text-xs text-gray-500 mt-1" translate="no">
              {editHint}
            </div>
          )}
        </div>
      ) : (
        // En modo creación, mostrar todos los días de la semana
        days.map((day) => (
          <label
            key={day.date}
            className={`border-2 rounded-lg p-3 transition-colors block ${
              day.disabled
                ? "border-red-200 bg-red-50 cursor-not-allowed opacity-75"
                : selected.includes(day.date)
                ? "border-primary bg-primary-soft cursor-pointer"
                : "border-gray-200 hover:border-gray-300 cursor-pointer"
            }`}
          >
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={selected.includes(day.date)}
                onCheckedChange={() => onToggle(day.date)}
                className="h-4 w-4"
                disabled={day.disabled}
              />
              <Calendar
                className={`h-4 w-4 ${
                  day.disabled ? "text-red-400" : "text-primary"
                }`}
              />
              <div className="flex-1">
                <span
                  className={`font-medium notranslate ${
                    day.disabled ? "text-red-600" : ""
                  }`}
                  translate="no"
                >
                  {day.label}
                </span>
                {day.disabled && day.reason && (
                  <div className="text-xs text-red-500 mt-1">
                    {day.reason}
                  </div>
                )}
                {!day.disabled && day.hint && (
                  <div className="text-xs text-gray-500 mt-1" translate="no">
                    {day.hint}
                  </div>
                )}
              </div>
            </div>
          </label>
        ))
      )}
    </div>
  </div>
);
